import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import * as Yup from 'yup';

import Auth from '../../../api/auth';
import { FormModal } from '../../../components/Formik';
import { regexValidator } from '../../../helpers/regexValidator';
import useApiQuery from '../../../hooks/useApiQuery';

import type { APIHookArg } from '@/types';
import type { BusinessUnit } from '@/types/businessUnitTypes';
import type { FormikFormField } from '@/types/formTypes';
import type { TechnicalLead } from '@/types/user';

interface CreateProjectFormModalProps {
  onSubmit: (data: unknown) => void;
  onCancel: () => void;
}

const CreateProjectFormModal = ({ onSubmit, onCancel }: CreateProjectFormModalProps) => {
  const { organizationId } = useParams();

  const [technicalLeads, setTechnicalLeads] = useState<TechnicalLead[] | null>(null);

  const { error: fetchError, makeApiCall: getUsersCall } = useApiQuery(Auth.fetchV2Users);
  const { data: businessUnits, makeApiCall: getBusinessUnitsCall } = useApiQuery<BusinessUnit[]>(
    Auth.fetchV1BusinessUnits as unknown as (args: unknown) => Promise<BusinessUnit[]>
  );

  const { makeApiCall: createProjectCall } = useApiQuery(
    Auth.createV2Project as unknown as APIHookArg,
    {
      successMessage: 'projectCreation',
    }
  );

  useEffect(() => {
    const getBusinessUnits = async () => {
      await getBusinessUnitsCall({ organizationId });
    };

    const getTechnicalLeads = async () => {
      const data = (await getUsersCall({ organizationId, roles: ['TECHNICAL_LEAD'] })) as
        TechnicalLead[] | null;

      if (data) {
        const activatedTechnicalLeads = data.filter(technicalLead => technicalLead.activated);
        setTechnicalLeads(activatedTechnicalLeads);
      }
    };

    getBusinessUnits();
    getTechnicalLeads();
  }, [getBusinessUnitsCall, getUsersCall, organizationId]);

  const fields = [
    {
      fieldType: 'text',
      name: 'name',
      label: 'Project Name',
    },
    {
      fieldType: 'text',
      name: 'description',
      label: 'Description',
    },
    {
      fieldType: 'select',
      name: 'businessUnitId',
      label: 'Business Unit',
      menuItems: businessUnits?.map(({ _id, name }) => ({ value: _id, label: name })),
    },
    {
      fieldType: 'select',
      name: 'technicalLeadId',
      label: 'Technical Lead',
      menuItems: technicalLeads?.map(({ _id, email }) => ({ value: _id, label: email })),
    },
  ];

  const initialValues = {
    name: '',
    description: '',
    businessUnitId: null,
    technicalLeadId: null,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Required')
      .matches(regexValidator.name, 'All lower-case, no spaces, no special characters (dash only)'),
    description: Yup.string().required('Required'),
  });

  const createProject = async (formValues: Record<string, unknown>) => {
    onCancel();

    const data = await createProjectCall({
      ...formValues,
      organizationId,
    });

    if (data) {
      onSubmit(data);
    }
  };

  return (
    <FormModal
      title='Create New Project'
      fields={fields as FormikFormField[]}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        createProject(values);
      }}
      onCancel={onCancel}
      submitLabel='Create'
      error={fetchError}
    />
  );
};

CreateProjectFormModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default CreateProjectFormModal;
